import React, { useState, useMemo, useEffect } from 'react';
import { Select, Space, Divider } from 'antd';
import PropTypes from 'prop-types';
import useIntegrationStores from 'modules/apps/api/useIntegrationStores';
import { LoadingOutlined } from '@ant-design/icons';
import debounce from 'common/debounce';
import StoreSwitcher from './component/StoreSwitcher';

const StoreSelect = (props) => {
  const {
    handleChanges,
    value = [],
    integration = 'all',
    disableSelectAll,
    disableStoreSwitch,
    preload = 20,
  } = props;
  const [search, setSearch] = useState();
  const [perPage, setPerPage] = useState(preload);
  const [scrollPosition, setScrollPosition] = useState(0);

  const [selectedApp, setSelectedApp] = useState(integration);
  const { stores, isLoading } = useIntegrationStores({
    integration: selectedApp === 'all' ? undefined : selectedApp,
    filters: {
      name: search,
      // integration_ids: [],
    },
    per_page: perPage,
  });

  useEffect(() => {
    if (scrollPosition > 90 && !(perPage > stores.length)) {
      setTimeout(() => {
        setPerPage(perPage + 20);
      }, 200);
    }
  }, [scrollPosition]);

  const handleSearch = debounce((searchValue) => {
    setSearch(searchValue);
  }, 500);

  const onSearch = (value) => {
    handleSearch(value);
  };

  useEffect(() => {
    setSelectedApp(integration);
  }, [integration]);

  const options = useMemo(() => {
    return stores.filter((opt) => selectedApp === 'all' || opt.type === integration);
  }, [stores, selectedApp, search, integration]);

  const handleChange = (value) => {
    handleChanges?.(value);
  };

  const handleIntegrationChange = (app) => {
    setSelectedApp(app);
  };

  const selectAllChanged = (e) => {
    if (e.target.checked) {
      handleChange?.(options.map((opt) => opt.id));
    } else {
      handleChange?.([]);
    }
  };

  const allSelected = options?.length === value?.length;

  return (
    <Select
      value={value}
      className='w-full'
      placeholder='Select Store'
      onChange={handleChange}
      mode='multiple'
      maxTagCount='responsive'
      onSearch={onSearch}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
        if (scrollPercentage > 90) {
          setScrollPosition(scrollPercentage);
        }
      }}
      dropdownRender={(menu) => (
        <>
          <StoreSwitcher
            disableSelectAll={disableSelectAll}
            allSelected={allSelected}
            selectAllChanged={selectAllChanged}
            selectedApp={selectedApp}
            disableStoreSwitch={disableStoreSwitch}
            handleIntegrationChange={handleIntegrationChange}
            options={options}
          />
          <Divider className='my-2' />
          <div className='transition-all duration-300 '>
            {menu}
            {isLoading && stores.length ? (
              <div className='w-full m-auto text-center'>
                <LoadingOutlined className='m-auto transition-all duration-300' />
              </div>
            ) : null}
          </div>
        </>
      )}
      {...props}
    >
      {options.map((opt) => (
        <Select.Option value={opt.id} key={opt.id} label={opt.name}>
          <Space>
            <span role='img' aria-label={opt.name}>
              <img src={`/icons/integrations/${opt.type}.svg`} className='w-5' />
            </span>
            {opt.name}
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
};

export default StoreSelect;

StoreSelect.propTypes = {
  handleChanges: PropTypes.func.isRequired,
  value: PropTypes.array.isRequired,
};
